import { ReactNode, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Button, Flex, Typography } from "antd";
import styled from "styled-components";
import { Select } from "@libertify/ui";
import { MediaProjectListItem } from "pages/media/types";
import { ProjectStatusTag } from "../../../media/projects/components/ProjectStatusTag";

export type ExistingProjectSelectProps = {
  projects: MediaProjectListItem[];
  onAttach: (project: MediaProjectListItem) => void;
};

export const ExistingProjectSelect = ({
  projects,
  onAttach,
}: ExistingProjectSelectProps) => {
  const t = useTranslate();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null
  );

  const optionRender = ({
    value,
    label,
  }: {
    value?: string | number;
    label?: ReactNode;
  }) => {
    const project = projects.find((p) => p.id === value);
    return project ? <ExistingProjectOption project={project} /> : label;
  };

  const handleAttach = () => {
    const project = projects.find((p) => p.id === selectedProjectId);
    if (project) onAttach(project);
  };

  return (
    <Flex gap={8}>
      <Select
        size="l"
        placeholder={t("lti.deepLink.existingProjectPlaceholder")}
        value={selectedProjectId ?? undefined}
        onChange={setSelectedProjectId}
        options={projects.map((project) => ({
          value: project.id,
          label: project.title,
        }))}
        optionRender={optionRender}
        labelRender={optionRender}
      />
      <Button
        type="primary"
        size="large"
        disabled={!selectedProjectId}
        onClick={handleAttach}
      >
        {t("lti.deepLink.attachButton")}
      </Button>
    </Flex>
  );
};

// Only Ready/Live ever render here (filtered by the caller) — wide enough for
// both labels across all three locales, without clipping the longest one
// ("En direct"/fr).
const STATUS_TAG_MIN_WIDTH = 90;

// One Select option/label row: thumbnail + project title + status.
const ExistingProjectOption = ({
  project,
}: {
  project: MediaProjectListItem;
}) => (
  <Flex align="center" gap={8} style={{ minWidth: 0 }}>
    <StyledThumbnail src={project.thumbnail_url} />
    <Typography.Text
      ellipsis={{ tooltip: project.title }}
      style={{ flex: 1, minWidth: 0 }}
    >
      {project.title}
    </Typography.Text>
    <ProjectStatusTag
      status={project.status}
      style={{ minWidth: STATUS_TAG_MIN_WIDTH, width: "auto", flexShrink: 0 }}
    />
  </Flex>
);

// ─── Styled components ───────────────────────────────────────────────────────

const StyledThumbnail = styled.div<{ src?: string | null }>`
  width: 40px;
  aspect-ratio: 4/3;
  flex-shrink: 0;
  border-radius: 4px;
  background: ${({ src }) =>
    src
      ? `url(${src}) center center / cover no-repeat`
      : "linear-gradient(135deg, rgb(20, 184, 166), rgb(13, 148, 136))"};
`;
